import { makeCygnusApiCall } from "../../../api/api";
import { CustomToaster } from "../../../pages/reusable_components/Toasters/toaster";

/**
 * Fetches active content blocks used in engagement letters.
 * @returns List of content blocks
 */
export const fetchContentBlocksDetails = async (): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    url: "/engagement-letter/content_blocks/?Status=Active",
    show_message: false,
    customized_success_message: "Fetched content blocks successfully.",
    customized_failure_message: "Failed to fetch content blocks.",
    customized_error_message: "An error occurred while fetching content blocks.",
    isblob: false,
    forMDM: true,
  });

  if (response?.data) {
    return response.data;
  } else {
    throw new Error("No content block data found");
  }
};

/**
 * Fetches signature and selected fields for firm template.
 * @returns Signature and selected field details
 */
export const fetchFirmTemplateFieldDetails = async (): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    url: "/engagement-letter/firm_template_fields/",
    show_message: false,
    customized_success_message: "Fetched firm template field details.",
    customized_failure_message: "Failed to fetch firm template field details.",
    customized_error_message: "An error occurred while fetching template field details.",
    isblob: false,
    forMDM: true,
  });

  if (response?.data) {
    return response.data;
  } else {
    throw new Error("No firm template field data found");
  }
};

/**
 * Fetches rule details and merges local saved rules for a template.
 * @param templateId - The ID of the template
 * @returns Combined rule data from API and local storage
 */
export const fetchTemplateFieldRulesDetails = async (
  templateId: number | string,
  rulesData: any[]
): Promise<any[]> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    url: `/engagement-letter/template_rule_details/?TemplateId=${templateId}`,
    show_message: false,
    customized_success_message: "Fetched template field rules.",
    customized_failure_message: "Failed to fetch template field rules.",
    customized_error_message: "Error occurred while fetching template field rules.",
    isblob: false,
    forMDM: true,
  });

  const updatedRules = [...(response?.data ?? []), ...rulesData];

  return updatedRules;
};

/**
 * Saves field rule configurations for the specified template.
 * @param templateId - The ID of the new or edited template
 * @param rulesData - List of rule configuration objects
 * @returns Success status
 */
export const saveTemplateFieldConfigDetails = async (
  templateId: number,
  rulesData: any[]
): Promise<boolean> => {
  const payload = {
    FieldRulesConfigs: rulesData.map(rule => ({ ...rule, TemplateId: templateId })),
  };

  const response = await makeCygnusApiCall({
    type: "POST",
    url: "/engagement-letter/template_rule_details/",
    payload,
    show_message: false,
    customized_success_message: "Template field config saved successfully.",
    customized_failure_message: "Failed to save template field config.",
    customized_error_message: "Error occurred while saving field configuration.",
    isblob: false,
    forMDM: true,
  });

  return response?.success ?? false;
};

/**
 * Saves or updates the engagement letter template file.
 * @param originalFileName - Name of the original uploaded file
 * @param base64Data - Base64 content of the DOCX file
 * @param rulesData - List of rules
 * @returns New template ID on success
 */
export const saveTemplateFile = async (
  templateId: number | string,
  originalFileName?: string,
  base64Data?: string,
  rulesData?: any[],
  recipients?: string,
  auto_approval?: string,
  bulk_upload?: string
) => {
  const requestBody: any = {
    FileName: `${localStorage.getItem("key")}.docx`,
    OriginalFileName: originalFileName,
    DocFileData: base64Data,
    Recipients: recipients,
    AutoApproval: auto_approval,
    BulkUpload: bulk_upload
  };

  if (templateId && templateId != -1) {
    requestBody.TemplateId = templateId;
  }

  const success_type = base64Data ? "Template finalized" : "Settings updated";
  const failure_type = base64Data ? "finalize template" : "update settings";

  const response = await makeCygnusApiCall({
    type: "POST",
    url: "/engagement-letter/templates/",
    payload: requestBody,
    show_message: true,
    customized_success_message: `${success_type} successfully.`,
    customized_failure_message: `Failed to ${failure_type}.`,
    customized_error_message: "Error occurred while finalizing template.",
    isblob: false,
    forMDM: true,
  });
};

/**
 * Saves or updates the processed engagement letter template.
 *
 * If only signature status needs to be updated, pass:
 *    saveProcessedTemplateFile(processedTemplateId, null, null, null, null, { SignatureStatus: "Awaiting WetSign" });
 *
 * @param processedTemplateId - ID of the processed template to update (required)
 * @param docFileName - File name for the DOCX file (optional)
 * @param pdfFileName - File name for the PDF file (optional)
 * @param base64Data - Base64 content of the DOCX file (optional)
 * @param clientDetails - Object containing client-related details (optional)
 * @param extraFields - Optional additional fields (e.g., SignatureStatus)
 * @returns Success boolean
 */
export const saveProcessedTemplateFile = async (
  processedTemplateId: number,
  docFileName?: string | null,
  pdfFileName?: string | null,
  base64Data?: string | null,
  clientDetails?: Record<string, any> | null,
  extraFields: Record<string, any> = {}
): Promise<boolean> => {

  const isMetadataOnlyUpdate =
    !base64Data || base64Data === "" || base64Data === null;
  const requestBody: any = {
    ProcessedTemplateId: processedTemplateId,
    DocFileName: docFileName,
    FileName: pdfFileName,
    DocFileData: base64Data,
    ClientDetails: clientDetails,
    ...extraFields,
  };

  const response = await makeCygnusApiCall({
    type: "PUT",
    url: "/engagement-letter/processed_templates/",
    payload: requestBody,
    show_message: true,
    customized_success_message: isMetadataOnlyUpdate
      ? "Engagement Letter updated successfully."
      : "Letter finalized successfully.",
    customized_failure_message: isMetadataOnlyUpdate
      ? "Failed to update Engagement Letter."
      : "Failed to finalize letter.",
    customized_error_message: isMetadataOnlyUpdate
      ? "Error occurred while updating the Engagement Letter."
      : "Error occurred while finalizing the letter.",
    isblob: false,
    forMDM: true,
  });

  if (response?.success) {
    return true;
  } else {
    throw new Error("Processed template save failed");
  }
};

/**
 * Converts a DOCX file (base64) to a PDF blob URL.
 * @param base64Data - Base64 content of the DOCX file
 * @returns URL to the converted PDF blob
 */
export const convertDocToPdf = async (base64Data: string): Promise<string> => {
  const requestBody = { base64Data };

  const url = `${process.env.REACT_APP_CONVERSION_API_BASE_URI}/api/pdfconversionfunction`;

  const response = await makeCygnusApiCall({
    type: "POST",
    url: url,
    payload: requestBody,
    show_message: false,
    customized_success_message: "DOCX converted to PDF successfully.",
    customized_failure_message: "Failed to convert DOCX to PDF.",
    customized_error_message: "Error occurred during DOCX to PDF conversion.",
    isblob: true,
    forMDM: false,
  });

  if (response instanceof Blob) {
    return window.URL.createObjectURL(response);
  } else {
    throw new Error("Invalid PDF conversion response");
  }
};

/**
 * Converts a DOCX file to SFDT format using either Azure Blob URL or base64 content.
 * @param docFileName - Name of the DOCX file
 * @param baseData - Base64 string of the DOCX file
 * @param editType - Type of document ("Template" or "EngagementLetter")
 * @param forwizard - Whether the editor is used in wizard mode
 * @param setOriginalSFDT - Setter function for SFDT string
 * @param containerRef - Ref to the document editor container
 * @param setLoadingEditor - Function to toggle editor loading state
 */
export const convertDocToSFDT = async ({
  docFileName,
  baseData,
  editType,
  forwizard,
  setOriginalSFDT,
  containerRef
}: {
  docFileName?: string;
  baseData?: string;
  editType: "Template" | "EngagementLetter";
  forwizard: boolean;
  setOriginalSFDT: (sfdt: string) => void;
  containerRef: any;
}): Promise<void> => {
  if (!docFileName && !baseData) return;

  const blobContainer =
    editType === "Template" ? "templatefiles" : "engagementletterdocfiles";

  const isUrl = typeof baseData === "string" && baseData.startsWith("http");

  const content = isUrl
    ? { fileUrl: baseData }
    : (!forwizard && docFileName
        ? { fileUrl: `${process.env.REACT_APP_AZURE_BLOB_BASE_URI}/${blobContainer}/${docFileName}` }
        : { base64Data: baseData });

  const url = `${process.env.REACT_APP_CONVERSION_API_BASE_URI}/api/SFDTConversionFunction`;

  try {
    const response = await makeCygnusApiCall({
      type: "POST",
      url: url,
      payload: content,
      show_message: false,
      customized_failure_message: "Failed to convert document to SFDT.",
      customized_error_message: "Error during SFDT conversion.",
      isblob: false,
      forMDM: false,
      rawTextResponse: true,
    });

    if (response) {
      setOriginalSFDT(response);
      containerRef.current.documentEditor.open(response);
    }
  } catch (error) {
    console.error("Error during conversion:", error);
  }
};
